// =============================================================================
//  ejerciciosPegados.js
//  TODOS LOS EJERCICIOS DE UN VÍDEO, DE UNA SOLA PEGADA
//
//  Rob, 07-10-2026: «si quiero hacer los ejercicios en otra IA para validar y
//  crear los ejercicios completos, ¿se puede crear un botón con el formato
//  esperado de copiar y pegar, y que tenga el minuto, segundo, milisegundo
//  exacto, para pasar todos los ejercicios que necesito de una sola vez en vez
//  de estar pegando y copiando cada uno, que me lleva mucho tiempo y a veces
//  cometo errores?».
//
//  ── POR QUÉ JSON Y NO UN FORMATO BONITO ─────────────────────────────────────
//  Porque lo escribe una máquina y lo lee una máquina, y entre las dos no puede
//  haber interpretación. Un formato de líneas y guiones se lee mejor con los
//  ojos, pero basta un guion de más, una comilla curva o un salto de línea en
//  mitad de una pregunta para que el sitio donde acaba un ejercicio deje de
//  estar claro — y entonces lo que entra en el vídeo del alumno es cualquier
//  cosa, en silencio.
//
//  Con JSON, o se entiende entero o no se entiende: no hay término medio. Y
//  cualquier IA lo escribe sin que haya que explicarle nada.
//
//  ── LA REGLA DE ESTE FICHERO ────────────────────────────────────────────────
//  Nada se coloca «más o menos». Un ejercicio que no cumple lo que su tipo
//  necesita NO entra, y se dice cuál era y qué le faltaba. Meterlo a medias es
//  lo mismo que no revisarlo: sale verde en el editor y revienta —o aburre— en
//  el juego del alumno.
// =============================================================================

/** 0:07.4 · 3:12.0 — los mismos que enseña el editor. */
export function reloj(segundos) {
    const s = Math.max(0, Number(segundos) || 0);
    const enteros = Math.floor(s);
    const d = Math.round((s - enteros) * 10);
    const m = Math.floor(enteros / 60);
    const r = enteros % 60;
    return `${m}:${String(r).padStart(2, '0')}.${d}`;
}

const TIPOS = ['truefalse', 'quiz', 'fill', 'wordselect', 'match', 'dragdrop'];

const NOMBRE = {
    truefalse: 'Verdadero/Falso', quiz: 'Quiz', fill: 'Completar',
    wordselect: 'Vocabulario', match: 'Parejas', dragdrop: 'Drag & Drop',
};

// ═════════════════════════════════════════════════════════════════════════════
//  LO QUE SE LE PIDE A LA OTRA IA
// ═════════════════════════════════════════════════════════════════════════════

const INSTRUCCIONES = `Eres el que escribe los ejercicios de un vídeo para una
escuela de inglés. Abajo va el vídeo partido en FRAGMENTOS: de cada uno tienes su
número, el minuto exacto en el que empieza y acaba, y su transcripción literal.

Devuélveme SÓLO un bloque JSON, sin texto antes ni después, con esta forma:

{
  "ejercicios": [
    { "fragmento": 3, "type": "quiz", ... },
    { "fragmento": 3, "type": "truefalse", ... }
  ]
}

"fragmento" es el número que ves abajo. Puedes poner VARIOS ejercicios en el
mismo fragmento: se jugarán seguidos, sin que el vídeo avance entre ellos.

EL "disparo" ES OBLIGATORIO. Es el segundo exacto en el que se para el vídeo y
sale la pregunta, contado desde el principio y con una décima: "disparo": 47.3

Y va SIEMPRE en el segundo en que ACABA el fragmento donde termina de decirse la
idea. Ese número lo tienes impreso en cada fragmento de abajo, a la derecha:

    ### FRAGMENTO 12  ·  0:39.0 → 0:44.0  ·  (39.0s → 44.0s)
                                                      ↑
                                        eso es lo que va en "disparo"

Así el alumno oye la idea ENTERA y la pregunta entra justo en el corte. Si lo
pones antes, se le para el vídeo a mitad de la frase sobre la que le preguntas.

── LOS SEIS TIPOS, CON TODOS SUS CAMPOS ───────────────────────────────────────

{ "fragmento": 1, "type": "quiz",
  "question": "la pregunta",
  "choices": ["A", "B", "C", "D"],
  "correct": 2,
  "explanation": "por qué es la C" }
  · "correct" es la POSICIÓN en "choices", empezando por 0.
  · Cuatro opciones. Sólo una puede ser defendible.

{ "fragmento": 1, "type": "truefalse",
  "statement": "la afirmación",
  "answer": true,
  "explanation": "por qué" }

{ "fragmento": 1, "type": "fill",
  "template": "The ____ opened the ____ before sunrise.",
  "blanks": ["team", "doors"],
  "choices": [["team", "crew", "staff"], ["doors", "gates", "windows"]],
  "explanation": "por qué" }
  · Un ____ por hueco, y tantos "blanks" como huecos.
  · "choices" es una lista POR HUECO, y la correcta va dentro.
  · Las palabras correctas tienen que aparecer tal cual en la transcripción.

{ "fragmento": 1, "type": "wordselect",
  "targetWords": ["sunrise", "prototype"],
  "explanation": "qué significan" }
  · Palabras que estén EN la transcripción de ese fragmento, tal cual.

{ "fragmento": 1, "type": "match",
  "pairs": [ { "left": "sunrise", "right": "when the sun comes up" },
             { "left": "rehearse", "right": "to practise beforehand" } ],
  "explanation": "por qué" }
  · Mínimo dos parejas.

{ "fragmento": 1, "type": "dragdrop",
  "template": "The ____ opened the lab doors.",
  "correctWords": ["team"],
  "distractors": ["crew", "staff"],
  "explanation": "por qué" }

── PRIMERO ENTIENDE EL VÍDEO, DESPUÉS ESCRIBE ────────────────────────────────
0. Léelo ENTERO antes de escribir nada y decide de qué va: quién habla, qué está
   pasando, cómo avanza la historia y cuáles son los momentos que de verdad
   importan. Las preguntas salen de ESO.

   No vayas fragmento a fragmento sacando un dato de cada uno. Un ejercicio que
   sólo repite la última frase que se oyó no mide si el alumno entendió el vídeo:
   mide si estaba despierto hace tres segundos. Pregunta por lo que PASA —quién
   hace qué, por qué, qué cambió—, siempre con las palabras del vídeo.

   Tú decides cuántos y dónde dentro de lo que se pide más abajo. Si un trozo no
   da para una buena pregunta, no la inventes: salta a otro.

── DE DÓNDE SALE LA RESPUESTA ────────────────────────────────────────────────
1. Los fragmentos de abajo son CORTES DE TIEMPO, no unidades de significado.
   Muchos duran dos segundos y no dicen nada por sí solos —«all at the same
   time», «exercises our brain»—. Para construir una pregunta puedes y DEBES
   leer varios fragmentos seguidos.

   Lo que SÍ está prohibido es apoyarte en conocimiento general: todo lo que se
   pregunta tiene que estar dicho en el vídeo.

2. El "fragmento" y el "disparo" van juntos: el fragmento es aquel en el que
   TERMINA de decirse la idea, y el disparo es el segundo en que ESE fragmento
   acaba. Nunca antes: el alumno no puede tener que responder algo que todavía
   no ha oído.

── CUÁNTOS Y DÓNDE ───────────────────────────────────────────────────────────
3. {CUANTOS}. NO uno por fragmento: menos y mejores.
4. Repartidos a lo largo del vídeo, más o menos uno cada 20-30 segundos. Nada de
   amontonarlos al principio.
5. Variados: al menos cuatro de los seis tipos. No seis quiz seguidos.
   De «wordselect», poco: sólo si una palabra concreta es de verdad la clave de
   ese trozo. Marcar palabras sueltas no demuestra que se haya entendido nada.
6. Salta el relleno: los rótulos de sección, los saludos y las despedidas.

── EL NIVEL MANDA ────────────────────────────────────────────────────────────
7. Arriba pone el nivel de ESTE vídeo. No es un adorno: cambia qué se pregunta y
   cómo se escribe.

   · A1-A2 (básico): lo que se ve y se oye literal —quién, qué, dónde, cuántos—.
     Preguntas de una línea, con las palabras exactas del vídeo. Nada de ironía,
     nada de deducir.
   · B1-B2 (intermedio): lo que pasa y por qué. Se puede pedir relacionar dos
     momentos del vídeo o entender una intención dicha con todas sus letras. La
     respuesta sigue estando DICHA, pero no siempre en la misma frase.
   · C1-C2 (avanzado): el tono, la intención, la ironía, lo que se da a entender
     sin decirlo, y vocabulario de verdad. Aquí sí caben los matices.

   Si dudas, baja medio nivel: un ejercicio demasiado fácil aburre, pero uno
   demasiado difícil hace que el alumno cierre la aplicación.

── CÓMO TIENE QUE SER CADA UNO ───────────────────────────────────────────────
8. Una sola respuesta defendible. Los distractores, claramente falsos.
9. REPARTE LA RESPUESTA CORRECTA. En un «quiz», "correct" tiene que caer unas
   veces en 0, otras en 1, otras en 2 y otras en 3, más o menos por igual, y
   nunca la misma posición tres veces seguidas.

   No es una manía: si la correcta es siempre la primera, el alumno lo nota a la
   tercera pregunta y deja de escuchar el vídeo —pulsa la primera y acierta—. El
   ejercicio deja de medir nada y el vídeo deja de servir para nada.
10. La explicación dice POR QUÉ y no contradice a la respuesta marcada. Y no
   nombra la letra ni la posición: di qué dice el vídeo, no «es la C».
11. Los distractores tienen que ser palabras del propio vídeo o de uso corriente
   — nunca vocabulario más difícil que el original.
12. No repitas la misma pregunta en dos fragmentos.
13. Nada de texto fuera del JSON. Ni saludo, ni resumen, ni comentarios.`;

/**
 * ¿Están todas las respuestas correctas en el mismo sitio?
 *
 * Rob, 14-10-2026, con catorce ejercicios recién pegados: «todas las respuestas
 * del quiz son A, eso se tiene que aclarar en el prompt para que mixee las
 * respuestas correctas».
 *
 * Está en el prompt, y aun así hay que MIRARLO: una instrucción que se le pide a
 * otra IA no es una garantía, es una petición. Y es de las cosas que no se ven
 * revisando los ejercicios de uno en uno —cada uno está bien—; sólo se ven
 * contándolos todos juntos, que es justo lo que hace esto.
 *
 * Si la correcta cae siempre en el mismo sitio, el alumno lo nota a la tercera
 * pregunta y deja de escuchar el vídeo: pulsa la primera y acierta.
 *
 * Devuelve `null` cuando no hay nada que decir.
 */
export function sesgoDeLasRespuestas(ejercicios) {
    const quizzes = (Array.isArray(ejercicios) ? ejercicios : [])
        .map((e) => e?.data)
        .filter((d) => d && d.type === 'quiz' && Array.isArray(d.choices)
                 && Number.isInteger(d.correct));
    //  Con menos de cuatro no hay patrón que ver: tres primeras seguidas en un
    //  vídeo de tres preguntas es casualidad, no un vicio.
    if (quizzes.length < 4) return null;

    const cuenta = [0, 0, 0, 0];
    for (const d of quizzes) {
        if (d.correct >= 0 && d.correct < 4) cuenta[d.correct] += 1;
    }
    const mayor = Math.max(...cuenta);
    const donde = cuenta.indexOf(mayor);
    //  Dos tercios en la misma letra ya es un patrón que se aprende. Con enteros
    //  y no con `0.67`: cuatro de seis son dos tercios EXACTOS, y escrito en coma
    //  flotante se quedaba fuera por una milésima.
    if (mayor * 3 < quizzes.length * 2) return null;

    return {
        cuantos: mayor,
        total: quizzes.length,
        letra: 'ABCD'[donde],
        reparto: cuenta,
    };
}

/**
 * El encargo completo: instrucciones + el vídeo partido en fragmentos.
 */
export function plantillaParaOtraIA({ meta = {}, segments = [] } = {}) {
    const linea = '='.repeat(74);
    const fuera = [];

    fuera.push(linea);
    fuera.push('  NSL — ESCRIBIR LOS EJERCICIOS DE UN VÍDEO');
    fuera.push(linea);
    fuera.push('');
    //  ── CUÁNTOS EJERCICIOS PEDIR (13-10-2026) ────────────────────────
    //  Rob montó un vídeo de cinco minutos que salió en 95 fragmentos de dos o
    //  tres segundos. Sin decirle un número, la otra IA adivina: puede devolver
    //  ocho o puede devolver sesenta, y sesenta en cinco minutos es un
    //  interrogatorio, no una lección.
    //
    //  Se calcula por la DURACIÓN, no por los fragmentos: el troceo depende de
    //  cómo reparta los sellos de tiempo YouTube, y dos vídeos de la misma
    //  duración pueden salir en veinte fragmentos o en cien. Lo que no cambia es
    //  cuánto aguanta un alumno: uno cada veinte o treinta segundos.
    const finalDelVideo = segments.reduce(
        (t, sg) => Math.max(t, Number(sg?.endTime) || 0), 0);
    const minutos = finalDelVideo / 60;
    //  Con techo: uno cada veinte segundos está bien en cinco minutos y es un
    //  interrogatorio en quince. Cada ejercicio para el vídeo, y veinte paradas
    //  en una sesión son más de las que aguanta nadie.
    const entre = (n, min, max) => Math.min(Math.max(Math.round(n), min), max);
    const pocos = entre(minutos * 2, 4, 14);
    const muchos = entre(minutos * 2.8, 6, 18);
    const cuantos = finalDelVideo > 0
        ? `Quiero entre ${pocos} y ${muchos} ejercicios para todo el vídeo, `
          + `que dura ${reloj(finalDelVideo)}`
        : 'Quiero entre 10 y 14 ejercicios para todo el vídeo';

    fuera.push(INSTRUCCIONES.replace('{CUANTOS}', cuantos));
    fuera.push('');
    fuera.push('-'.repeat(74));
    fuera.push(`Vídeo      : ${(meta.title || '').trim() || '(sin título)'}`);
    fuera.push(`Canal      : ${(meta.channel || '').trim() || '(sin canal)'}`);
    fuera.push(`Nivel      : ${meta.level || '?'} · ${meta.language || '?'}`);
    fuera.push(`Fragmentos : ${segments.length}`);
    fuera.push('-'.repeat(74));

    segments.forEach((seg, i) => {
        const ini = Number(seg?.startTime) || 0;
        const fin = Number(seg?.endTime) || 0;
        fuera.push('');
        //  Los dos: el minuto para leerlo y los segundos crudos para que la IA
        //  pueda calcular un «disparo» sin tener que convertir nada.
        fuera.push(`### FRAGMENTO ${i + 1}  ·  ${reloj(ini)} → ${reloj(fin)}  `
            + `·  (${ini.toFixed(1)}s → ${fin.toFixed(1)}s)`);
        if ((seg?.titulo || '').trim()) fuera.push(`CAPÍTULO: ${seg.titulo.trim()}`);
        fuera.push((seg?.text || '').trim() || '(este fragmento no tiene texto)');

        //  Lo que ya hay, para que la otra IA no repita lo que está hecho.
        const hechos = (seg?.exercises || [])
            .filter(e => e?.data)
            .map(e => NOMBRE[e.data.type] || e.data.type);
        if (hechos.length) fuera.push(`(ya tiene: ${hechos.join(', ')})`);
    });

    fuera.push('');
    return fuera.join('\n');
}

// ═════════════════════════════════════════════════════════════════════════════
//  LEER LO QUE CONTESTÓ
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Saca el JSON de lo que sea que haya pegado.
 *
 * Una IA casi nunca devuelve JSON a secas: lo envuelve en ```json, le pone un
 * «aquí tienes» delante y un «¿quieres que…?» detrás, y a veces usa comillas
 * curvas porque el editor de donde se copió las cambió. Pedirle a Rob que
 * limpie eso a mano es devolverle el trabajo que esto le quita.
 */
export function sacaElJson(texto) {
    if (typeof texto !== 'string' || !texto.trim()) return null;

    //  Las vallas de código, con o sin «json» detrás.
    const limpio = texto.replace(/```[a-zA-Z]*\n?/g, '').replace(/```/g, '');

    //  ── EL ORDEN IMPORTA (07-10-2026) ────────────────────────────────────
    //  Primero se intenta tal cual, SIN tocar las comillas curvas.
    //
    //  Antes se cambiaban “” por " lo primero de todo, pensando en la IA que las
    //  usa para abrir y cerrar los campos. Pero una IA que explica un vídeo CITA
    //  el vídeo: «the speaker says, “My dream team.”». Esas comillas van DENTRO
    //  del texto, donde son un carácter cualquiera y JSON las admite sin
    //  problema — y al cambiarlas por rectas cerraban el campo a mitad y
    //  reventaban un JSON que estaba perfecto.
    //
    //  Rob pegó once ejercicios válidos y le dijimos «no se encontró ningún JSON
    //  ahí dentro». Lo había roto esta función, no quien lo escribió.
    const intentos = [
        limpio,
        //  Y sólo si así no sale, con las curvas pasadas a rectas: ahora sí
        //  hablamos de la IA que las usó como delimitador. Sólo las dobles; las
        //  simples pueden estar en medio de una palabra inglesa —«don't»—.
        limpio.replace(/[“”]/g, '"'),
    ];

    for (const t of intentos) {
        //  Del primer `{` o `[` al último que cierre. Lo de fuera es charla.
        const ini = t.search(/[{[]/);
        if (ini === -1) continue;
        const cierra = t[ini] === '{' ? '}' : ']';
        const fin = t.lastIndexOf(cierra);
        if (fin <= ini) continue;

        //  Comas de más antes de un cierre: el fallo más común de un JSON
        //  escrito por una IA, y el único que vale la pena perdonar — no cambia
        //  el sentido de nada.
        const trozo = t.slice(ini, fin + 1).replace(/,(\s*[}\]])/g, '$1');
        try {
            return JSON.parse(trozo);
        } catch { /* se prueba el siguiente intento */ }
    }

    //  Si ninguno parsea entero, se rescata lo que se pueda. Ver `rescata`.
    for (const t of intentos) {
        const rescatados = rescata(t);
        if (rescatados.length) return { ejercicios: rescatados, cortada: true };
    }
    return null;
}

/**
 * Saca los ejercicios uno a uno de un JSON que no parsea entero.
 *
 * Pasa con los vídeos largos: treinta y ocho fragmentos piden una respuesta de
 * miles de caracteres, y el chat de la otra IA la corta por la mitad. Entonces
 * el JSON no cierra, `JSON.parse` falla y se pierde TODO — incluidos los treinta
 * que sí habían llegado enteros.
 *
 * Esto recorre el texto contando llaves —respetando las que van dentro de un
 * texto entrecomillado, que si no, una pregunta con un `{` lo descuadraría— y se
 * queda con cada objeto que cierre bien y hable de un ejercicio. Lo que quedó a
 * medias se descarta solo: no parsea.
 */
export function rescata(texto) {
    const t = String(texto || '');
    const sacados = [];
    let i = 0;

    while (i < t.length) {
        if (t[i] !== '{') { i += 1; continue; }

        let hondo = 0;
        let dentroDeTexto = false;
        let escapado = false;
        let j = i;

        for (; j < t.length; j++) {
            const c = t[j];
            if (escapado) { escapado = false; continue; }
            if (c === '\\') { escapado = true; continue; }
            if (c === '"') { dentroDeTexto = !dentroDeTexto; continue; }
            if (dentroDeTexto) continue;
            if (c === '{') hondo += 1;
            else if (c === '}') {
                hondo -= 1;
                if (hondo === 0) break;
            }
        }

        //  Este `{` no cierra. NO se puede parar aquí: el primero de todos es
        //  el objeto de fuera —el que envuelve la lista— y en una respuesta
        //  cortada ése nunca cierra. Parando ahí no se rescataba ni uno, que es
        //  justo lo que hacía la primera versión de esto.
        if (hondo !== 0) { i += 1; continue; }
        const trozo = t.slice(i, j + 1);

        //  Un atajo antes de parsear: la respuesta entera son miles de
        //  caracteres y aquí se entra por cada `{`. La guarda de verdad es el
        //  `o.type` de abajo.
        let esEjercicio = false;
        if (/"type"\s*:/.test(trozo)) {
            try {
                const o = JSON.parse(trozo.replace(/,(\s*[}\]])/g, '$1'));
                if (o && typeof o === 'object' && o.type) {
                    sacados.push(o);
                    esEjercicio = true;
                }
            } catch { /* ése se quedó a medias */ }
        }

        //  Un ejercicio no lleva otro dentro: se salta entero.
        //
        //  Pero si NO era un ejercicio —el objeto de fuera, el que envuelve la
        //  lista— hay que meterse dentro, no saltárselo. Saltándolo se perdían
        //  los once que llevaba: el envoltorio cierra bien, así que se tragaba
        //  el documento completo de una vez y el bucle terminaba ahí.
        i = esEjercicio ? j + 1 : i + 1;
    }
    return sacados;
}

const esTexto = (v) => typeof v === 'string' && v.trim().length > 0;

/**
 * Arregla lo que una IA escribe mal de forma inofensiva, y SÓLO eso.
 *
 * Un `"correct": "2"` entrecomillado o un `"answer": "true"` son el descuido más
 * común de un JSON escrito por un modelo, y no hay ninguna duda sobre lo que
 * quería decir. Dejarlo entrar tal cual rompe el ejercicio en silencio —el
 * reproductor compara con `===` y entonces no hay respuesta correcta—, y
 * tirarlo por eso le cuesta a Rob un ejercicio por una comilla.
 *
 * Lo que NO se toca es nada que exija adivinar: un hueco que falta, una opción
 * vacía, una pareja a medias. Eso se queda fuera y se dice.
 */
export function normaliza(crudo) {
    if (!crudo || typeof crudo !== 'object') return crudo;
    const ej = { ...crudo };
    if (ej.type === 'quiz' && typeof ej.correct === 'string' && /^\d+$/.test(ej.correct.trim())) {
        ej.correct = Number(ej.correct.trim());
    }
    if (ej.type === 'truefalse' && typeof ej.answer === 'string') {
        const v = ej.answer.trim().toLowerCase();
        if (v === 'true' || v === 'verdadero') ej.answer = true;
        else if (v === 'false' || v === 'falso') ej.answer = false;
    }
    return ej;
}
const huecosDe = (plantilla) => (String(plantilla || '').match(/____/g) || []).length;

/**
 * ¿Este ejercicio está completo para su tipo? Devuelve el motivo si no.
 *
 * Las mismas condiciones que exige el servidor para dejarlo llegar al alumno.
 * Comprobarlas AQUÍ es la diferencia entre enterarse al pegar y enterarse
 * cuando un alumno abre el vídeo y no está la pregunta.
 */
export function queLeFalta(ej) {
    if (!ej || typeof ej !== 'object') return 'no es un objeto';
    const t = ej.type;
    if (!TIPOS.includes(t)) return `tipo desconocido: ${t ?? '(ninguno)'}`;

    if (t === 'quiz') {
        if (!esTexto(ej.question)) return 'le falta «question»';
        if (!Array.isArray(ej.choices) || ej.choices.length < 2) return 'necesita al menos dos «choices»';
        if (!ej.choices.every(esTexto)) return 'alguna opción está vacía';
        //  Un número, no un texto que lo parezca. El reproductor compara con
        //  `===`, así que un «2» entrecomillado no es igual a 2 y NINGUNA
        //  opción saldría marcada: el alumno no podría acertar. Lo arregla
        //  `normaliza`, antes de llegar aquí.
        const c = ej.correct;
        if (!Number.isInteger(c) || c < 0 || c >= ej.choices.length) {
            return `«correct» vale ${ej.correct} y no señala ninguna de las ${ej.choices.length} opciones`;
        }
        return null;
    }
    if (t === 'truefalse') {
        if (!esTexto(ej.statement)) return 'le falta «statement»';
        //  Lo mismo: el reproductor compara con `===` contra `true`/`false`.
        if (typeof ej.answer !== 'boolean') return '«answer» tiene que ser true o false';
        return null;
    }
    if (t === 'fill') {
        if (!esTexto(ej.template)) return 'le falta «template»';
        const n = huecosDe(ej.template);
        if (n === 0) return 'la plantilla no tiene ningún ____';
        if (!Array.isArray(ej.blanks) || ej.blanks.length !== n) {
            return `la plantilla tiene ${n} huecos y hay ${(ej.blanks || []).length} «blanks»`;
        }
        if (!ej.blanks.every(esTexto)) return 'algún hueco está vacío';
        if (!Array.isArray(ej.choices) || ej.choices.length !== n) {
            return `«choices» tiene que ser una lista por hueco (${n})`;
        }
        if (!ej.choices.every(c => Array.isArray(c) && c.length >= 2)) {
            return 'cada hueco necesita al menos dos opciones';
        }
        //  La correcta tiene que estar entre sus opciones, o el alumno no puede
        //  acertar ni queriendo.
        for (let i = 0; i < n; i++) {
            if (!ej.choices[i].includes(ej.blanks[i])) {
                return `la respuesta del hueco ${i + 1} («${ej.blanks[i]}») no está entre sus opciones`;
            }
        }
        return null;
    }
    if (t === 'wordselect') {
        if (!Array.isArray(ej.targetWords) || !ej.targetWords.length) return 'le faltan «targetWords»';
        if (!ej.targetWords.every(esTexto)) return 'alguna palabra está vacía';
        return null;
    }
    if (t === 'match') {
        if (!Array.isArray(ej.pairs) || ej.pairs.length < 2) return 'necesita al menos dos «pairs»';
        if (!ej.pairs.every(p => esTexto(p?.left) && esTexto(p?.right))) {
            return 'alguna pareja tiene un lado vacío';
        }
        return null;
    }
    // dragdrop
    if (!esTexto(ej.template)) return 'le falta «template»';
    const n = huecosDe(ej.template);
    if (n === 0) return 'la plantilla no tiene ningún ____';
    if (!Array.isArray(ej.correctWords) || ej.correctWords.length !== n) {
        return `la plantilla tiene ${n} huecos y hay ${(ej.correctWords || []).length} «correctWords»`;
    }
    if (!ej.correctWords.every(esTexto)) return 'alguna palabra está vacía';
    return null;
}

/**
 * Lee lo pegado y devuelve qué entra y qué no, SIN tocar nada todavía.
 *
 * Separado de colocarlo a propósito: primero se enseña lo que va a pasar y
 * después se hace. Pegar quinientas líneas y que la pantalla cambie sola no deja
 * revisar nada.
 */
export function leeEjercicios(texto, segments = []) {
    const datos = sacaElJson(texto);
    if (datos === null) {
        return {
            ok: false,
            motivo: 'No se encontró ningún JSON ahí dentro. Copia la respuesta '
                + 'entera de la otra IA, con sus llaves.',
            entran: [], fuera: [], cortada: false,
        };
    }

    const lista = Array.isArray(datos) ? datos
        : Array.isArray(datos.ejercicios) ? datos.ejercicios
        : Array.isArray(datos.exercises) ? datos.exercises
        : null;
    //  La respuesta venía cortada y se rescató lo que había llegado entero.
    const cortada = !Array.isArray(datos) && datos.cortada === true;

    if (!lista) {
        return {
            ok: false,
            motivo: 'El JSON no trae una lista «ejercicios». Tiene que ser '
                + '{ "ejercicios": [ … ] } o una lista a secas.',
            entran: [], fuera: [], cortada: false,
        };
    }
    if (!lista.length) {
        return { ok: false, motivo: 'La lista de ejercicios está vacía.',
                 entran: [], fuera: [], cortada: false };
    }

    const entran = [];
    const fuera = [];

    lista.forEach((crudo, i) => {
        const donde = `ejercicio ${i + 1}`;
        if (!crudo || typeof crudo !== 'object') {
            fuera.push({ donde, motivo: 'no es un objeto' });
            return;
        }
        //  El número que ve Rob en pantalla, de 1 en adelante.
        const n = Number(crudo.fragmento ?? crudo.fragment ?? crudo.segmento);
        if (!Number.isInteger(n) || n < 1 || n > segments.length) {
            fuera.push({
                donde,
                motivo: `«fragmento» vale ${crudo.fragmento ?? '(nada)'} y el vídeo `
                    + `tiene ${segments.length} fragmentos`,
            });
            return;
        }
        const limpio = normaliza(crudo);
        const falta = queLeFalta(limpio);
        if (falta) {
            fuera.push({ donde: `${donde} (fragmento ${n}, ${NOMBRE[crudo.type] || crudo.type || '?'})`,
                         motivo: falta });
            return;
        }

        //  Lo que se guarda es EXACTAMENTE la forma que ya maneja el editor, sin
        //  campos de más: `fragmento` y `disparo` son del transporte, no del
        //  ejercicio.
        const { fragmento, fragment, segmento, disparo, ...data } = limpio;
        const d = Number(disparo);
        if (Number.isFinite(d) && d >= 0) data.trigger_seconds = Math.round(d * 10) / 10;

        entran.push({ indice: n - 1, numero: n, data });
    });

    return {
        ok: entran.length > 0, entran, fuera, cortada, motivo: '',
        sesgo: sesgoDeLasRespuestas(entran),
    };
}

/**
 * Coloca lo leído en los fragmentos. Devuelve unos `segments` NUEVOS.
 *
 * @param {string} modo  'añadir' deja lo que ya hubiera; 'sustituir' lo quita.
 */
export function colocaEjercicios(segments, entran, modo = 'añadir') {
    const lista = Array.isArray(segments) ? segments : [];
    const porFragmento = new Map();
    for (const e of entran || []) {
        if (!porFragmento.has(e.indice)) porFragmento.set(e.indice, []);
        porFragmento.get(e.indice).push(e.data);
    }

    let semilla = Date.now();
    return lista.map((seg, i) => {
        const nuevos = porFragmento.get(i);
        if (!nuevos || !nuevos.length) {
            return modo === 'sustituir' ? { ...seg, exercises: [] } : seg;
        }
        const puestos = nuevos.map(data => ({
            //  Un identificador propio, distinto de los que crea el editor a
            //  mano: dos ejercicios con el mismo id hacen que borrar uno borre
            //  los dos.
            id: `peg${semilla++}`,
            type: data.type,
            data,
            status: 'ready',
        }));
        return {
            ...seg,
            exercises: modo === 'sustituir' ? puestos : [...(seg.exercises || []), ...puestos],
        };
    });
}
